<script setup lang="ts">
import { computed } from 'vue';
import { KIND_GROUPS, useItemsStore } from '../stores/items';
import { COLORS } from '../colors';
import type { Kind } from '../types';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const items = useItemsStore();

// Один источник правды по категориям — порядок и подписи из KIND_GROUPS.
const KINDS: { value: Kind | null; label: string }[] = [
  { value: null, label: 'Всё' },
  ...KIND_GROUPS.map((g) => ({ value: g.kind, label: g.label })),
];

const QUALITIES = [0, 1, 2, 3, 4];

function toggleQuality(q: number) {
  const i = items.filters.quality.indexOf(q);
  if (i >= 0) items.filters.quality.splice(i, 1);
  else items.filters.quality.push(q);
}

function togglePool(slug: string) {
  const i = items.filters.pools.indexOf(slug);
  if (i >= 0) items.filters.pools.splice(i, 1);
  else items.filters.pools.push(slug);
}

function toggleColor(slug: string) {
  const i = items.filters.colors.indexOf(slug);
  if (i >= 0) items.filters.colors.splice(i, 1);
  else items.filters.colors.push(slug);
}

function reset() {
  items.filters.kind = null;
  items.filters.quality = [];
  items.filters.pools = [];
  items.filters.colors = [];
}

const activeCount = computed(() =>
  (items.filters.kind ? 1 : 0)
  + items.filters.quality.length
  + items.filters.pools.length
  + items.filters.colors.length
);

defineExpose({ activeCount });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-40 bg-black/60"
      @click="emit('close')"
    />
    <Transition name="sheet">
      <div
        v-if="open"
        class="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-bg-elev pb-safe shadow-2xl"
      >
        <div class="sticky top-0 z-10 flex items-center justify-between border-b border-stone-800 bg-bg-elev px-4 py-3">
          <h2 class="text-base font-semibold">Фильтры</h2>
          <div class="flex gap-2">
            <button class="rounded-md px-3 py-1 text-sm text-stone-400 hover:text-stone-200" @click="reset">
              Сбросить
            </button>
            <button class="rounded-md bg-stone-700 px-3 py-1 text-sm text-stone-100" @click="emit('close')">
              Готово
            </button>
          </div>
        </div>

        <div class="space-y-5 p-4">
          <section>
            <h3 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Категория</h3>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="k in KINDS"
                :key="String(k.value)"
                @click="items.filters.kind = k.value"
                :aria-pressed="items.filters.kind === k.value"
                class="rounded-full px-3 py-1 text-sm transition-colors"
                :class="items.filters.kind === k.value
                  ? 'bg-stone-200 text-stone-900'
                  : 'bg-bg-card text-stone-300 hover:bg-stone-800'"
              >{{ k.label }}</button>
            </div>
          </section>

          <section>
            <h3 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Качество</h3>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="q in QUALITIES"
                :key="q"
                @click="toggleQuality(q)"
                :aria-pressed="items.filters.quality.includes(q)"
                class="h-9 w-9 rounded-full text-sm font-semibold transition-colors"
                :class="items.filters.quality.includes(q)
                  ? 'bg-stone-200 text-stone-900'
                  : 'bg-bg-card text-stone-300 hover:bg-stone-800'"
              >{{ q }}</button>
            </div>
          </section>

          <section>
            <h3 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Цвет</h3>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="c in COLORS"
                :key="c.slug"
                @click="toggleColor(c.slug)"
                :aria-pressed="items.filters.colors.includes(c.slug)"
                class="inline-flex items-center gap-1.5 rounded-full py-1 pl-1.5 pr-3 text-sm transition-colors ring-1"
                :class="items.filters.colors.includes(c.slug)
                  ? 'bg-stone-200 text-stone-900 ring-stone-200'
                  : 'bg-bg-card text-stone-300 ring-transparent hover:bg-stone-800'"
              >
                <span class="h-4 w-4 rounded-full ring-1 ring-white/20" :style="{ background: c.hex }" />
                {{ c.name }}
              </button>
            </div>
          </section>

          <section v-if="items.poolsVocab.length">
            <h3 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Пулы</h3>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="p in items.poolsVocab"
                :key="p.slug"
                @click="togglePool(p.slug)"
                :aria-pressed="items.filters.pools.includes(p.slug)"
                class="rounded-full px-3 py-1 text-xs transition-colors"
                :class="items.filters.pools.includes(p.slug)
                  ? 'bg-stone-200 text-stone-900'
                  : 'bg-bg-card text-stone-300 hover:bg-stone-800'"
              >{{ p.name_ru }}</button>
            </div>
          </section>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sheet-enter-active, .sheet-leave-active {
  transition: transform 0.2s ease;
}
.sheet-enter-from, .sheet-leave-to {
  transform: translateY(100%);
}
</style>
